import { cetusClmmSDK } from '@libs/cetus.client';
import { type SuiObjectResponse, type PaginatedObjectsResponse } from '@mysten/sui/client';
import { normalizeStructTag } from '@mysten/sui/utils';
import { defineModel } from 'foca';
import { Decimal, MathUtil } from 'parrot-sui-sdk';

export interface MyVaultsStruct {
  id: string;
  vault_id: string;
  position_share: string;
  liquidity: string;
  tick_lower_index: number;
  tick_upper_index: number;
  pool_id: string;
  vault_liquidity: string;
  current_sqrt_price: string;
  coin_type_a: string;
  coin_type_b: string;
}

interface MyVaultsField {
  fields: {
    id: {
      id: string;
    };
    position_share: string;
    vault_id: string;
  };
}

interface VaultField {
  fields: {
    pool: string;
    liquidity: string;
    id: {
      id: string;
    };
    total_share: string;
    positions: {
      fields: {
        coin_type_a: {
          fields: {
            name: string;
          };
        };
        coin_type_b: {
          fields: {
            name: string;
          };
        };
        tick_lower_index: {
          fields: {
            bits: number;
          };
        };
        tick_upper_index: {
          fields: {
            bits: number;
          };
        };
      };
    }[];
  };
}

interface PoolField {
  fields: {
    current_sqrt_price: string;
    id: {
      id: string;
    };
  };
}

const initialState: {
  myVault: Record<string, MyVaultsStruct[]>;
  time: Record<string, number>;
} = {
  myVault: {},
  time: {},
};

export const myVaultModel = defineModel('my-vault', {
  initialState,
  methods: {
    async getMyVaults(address: string, isReloading = false) {
      if (
        !isReloading &&
        this.state.myVault[address] &&
        Date.now() - this.state.time[address]! < 1000 * 60 * 5
      ) {
        return this.state.myVault[address];
      }

      let dynamicFields: PaginatedObjectsResponse | undefined;
      let data: Pick<MyVaultsStruct, 'id' | 'vault_id' | 'position_share'>[] = [];
      do {
        dynamicFields = await cetusClmmSDK.fullClient.getOwnedObjects({
          owner: address,
          cursor: dynamicFields?.nextCursor,
          filter: {
            StructType:
              '0xaf0babdb56090a40343ce4eb0f007c756969226887ad2536068889ad8ffcee96::cetus::VaultPosition',
          },
          options: {
            showContent: true,
          },
        });
        if (dynamicFields) {
          data = [
            ...data,
            ...dynamicFields.data.map((item) => {
              const field = item.data?.content as unknown as MyVaultsField;
              return {
                id: field.fields.id.id,
                vault_id: field.fields.vault_id,
                position_share: field.fields.position_share,
              };
            }),
          ];
        }
      } while (dynamicFields.hasNextPage);

      const vaultIds = data.map((item) => item.vault_id);
      const vaultsData = await this.getVaults(vaultIds);

      const final_data: Record<string, MyVaultsStruct[]> = {};
      final_data[address] = data.map((item) => {
        const vault = vaultsData.find((vault) => item.vault_id === vault.vault_id)!;
        return {
          ...item,
          ...vault,
          liquidity: new Decimal(item.position_share)
            .div(vault.total_share)
            .mul(vault.vault_liquidity)
            .toFixed(0),
        };
      });

      const final_time: Record<string, number> = {};
      final_time[address] = Date.now();

      this.setState({
        myVault: final_data,
        time: final_time,
      });

      return data;
    },

    async getVaults(vaultIds: string[]) {
      const max = 50;
      const len = Math.ceil(vaultIds.length / max);
      const final: SuiObjectResponse[] = [];

      for (let i = 0; i < len; i++) {
        const ids = vaultIds.slice(i * 50, (i + 1) * 50);
        const result = await cetusClmmSDK.fullClient.batchGetObjects(ids, {
          showContent: true,
        });
        final.push(...result);
      }
      const vaultsData = final.map((item) => {
        const field = item.data?.content as unknown as VaultField;
        return {
          vault_id: field.fields.id.id,
          vault_liquidity: field.fields.liquidity,
          pool_id: field.fields.pool,
          total_share: field.fields.total_share,
          tick_lower_index: MathUtil.asIntN(
            BigInt(field.fields.positions[0]!.fields.tick_lower_index.fields.bits),
          ),
          tick_upper_index: MathUtil.asIntN(
            BigInt(field.fields.positions[0]!.fields.tick_upper_index.fields.bits),
          ),
          coin_type_a: normalizeStructTag(
            field.fields.positions[0]!.fields.coin_type_a.fields.name,
          ),
          coin_type_b: normalizeStructTag(
            field.fields.positions[0]!.fields.coin_type_b.fields.name,
          ),
        };
      });
      const pools = vaultsData.map((item) => item.pool_id);
      const poolsData = await this.getPools(pools);

      return vaultsData.map((item) => {
        const pool = poolsData.find((pool) => pool.pool_id === item.pool_id);
        return {
          ...item,
          current_sqrt_price: pool!.current_sqrt_price,
        };
      });
    },

    async getPools(poolIds: string[]) {
      const max = 50;
      const len = Math.ceil(poolIds.length / max);
      const final: SuiObjectResponse[] = [];

      for (let i = 0; i < len; i++) {
        const ids = poolIds.slice(i * 50, (i + 1) * 50);
        const result = await cetusClmmSDK.fullClient.batchGetObjects(ids, {
          showContent: true,
        });
        final.push(...result);
      }
      const poolsData = final.map((item) => {
        const field = item.data?.content as unknown as PoolField;
        return {
          current_sqrt_price: field.fields.current_sqrt_price,
          pool_id: field.fields.id.id,
        };
      });
      return poolsData;
    },
  },
});
